import { useForm } from "react-hook-form"
import { useMutation } from "@tanstack/react-query"
import { useNavigate, Link } from "react-router"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { authProvider, type LoginPayload } from "@/providers/auth.provider"
import { useAuthStore } from "@/stores/auth.store"

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { register, handleSubmit } = useForm<LoginPayload>()

  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)

  const { mutate, isPending, error } = useMutation({
    mutationFn: (data: LoginPayload) => {
      if (!data.email.endsWith("@remap.club")) {
        throw new Error("Only @remap.club accounts are allowed")
      }
      return authProvider.login(data)
    },
    onSuccess: (user) => {
      setSession(user)
      navigate("/dashboard")
    },
  })

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle>Login to your account</CardTitle>
          <CardDescription>
            Enter your email below to login to your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit((data) => mutate(data))}>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  placeholder="m@example.com"
                  {...register("email", { required: true })}
                />
              </Field>
              <Field>
                <div className="flex items-center">
                  <FieldLabel htmlFor="password">Password</FieldLabel>
                  <Link
                    to="/auth/forgot-password"
                    className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                  >
                    Forgot your password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  {...register("password", { required: true })}
                />
              </Field>
              {error && (
                <p className="text-sm text-destructive">{error.message}</p>
              )}
              <Field>
                <Button type="submit" disabled={isPending}>
                  {isPending ? "Logging in…" : "Login"}
                </Button>
                <Button variant="outline" type="button">
                  Login with Google
                </Button>
                <FieldDescription className="text-center">
                  Don&apos;t have an account? <a href="#">Sign up</a>
                </FieldDescription>
              </Field>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
